// Axios and API_BASE_URL are imported dynamically in methods to avoid circular deps

// Updated RegularUser interface to match backend model
export interface RegularUser {
  id?: string;
  _id?: string;
  name: string;
  email: string;
  avatar?: string;
  isActive: boolean;
  isBanned: boolean;
  banReason?: string;
  bannedBy?: string | Record<string, any>;
  bannedAt?: Date | string;
  unbannedBy?: string | Record<string, any>;
  unbannedAt?: Date | string;
  createdAt: string;
  lastLogin?: string;
  /** خلاصهٔ کانال استفاده — از AnalyticsSession روی کاربر denormalize می‌شود */
  usage?: UserUsage;
  /** پروفایل تلگرامی (فقط برای کاربرانی که از ربات آمده‌اند) */
  telegram?: UserTelegramInfo;
}

/** برچسب کانالی که کاربر با آن کار می‌کند */
/**
 * `telegram` برای حساب‌هایی است که ربات تلگرام خودش ساخته (کسی که از پست
 * کانال وارد گفتگو شده ولی حساب کازماشاپ نداشته). این‌ها هرگز سشن وب/اپ ندارند،
 * پس تا قبل از این برای همیشه «نامشخص» نشان داده می‌شدند.
 */
export type UserChannel = 'app' | 'web' | 'both' | 'telegram';

/** پروفایل تلگرامی کاربر — تنها هویت حساب‌های مهمانِ ربات */
export interface UserTelegramInfo {
  telegramUserId: number;
  username: string | null;
  firstName: string | null;
  /** هنوز شماره‌اش را وصل نکرده — یعنی کاربر واقعی سایت نیست */
  isGuest: boolean;
  active: boolean;
  linkedAt: string | null;
  hasHub?: boolean;
  hub?: { chatId: number; title: string | null } | null;
  channels?: Array<{
    chatId: number;
    title: string | null;
    username: string | null;
    status: string;
    joinedAt: string | null;
    leftAt: string | null;
    viaLink: boolean;
    linkCode: string | null;
  }>;
  chats?: Array<{ id: string; title: string; at: string }>;
}

export interface UserUsage {
  /** پلتفرم‌های ریزدانه‌ای که تا امروز از آن‌ها وارد شده */
  platforms?: string[];
  lastPlatform?: string;
  primaryChannel?: UserChannel;
  lastActiveAt?: string;
  firstAppAt?: string;
  lastAppAt?: string;
  lastWebAt?: string;
  appSessions?: number;
  webSessions?: number;
  deviceCount?: number;
  appVersion?: string;
  appBuild?: string;
  appDeviceModel?: string;
  appOsVersion?: string;
}

export interface UserDevice {
  _id: string;
  deviceKey: string;
  platform: string;
  deviceModel?: string;
  deviceBrand?: string;
  osName?: string;
  osVersion?: string;
  browser?: string;
  deviceType?: string;
  appVersion?: string;
  appBuild?: string;
  installSource?: string;
  firstSeenAt: string;
  lastSeenAt: string;
  sessionCount: number;
}

export interface UserAnalyticsSession {
  sessionId: string;
  platform: string;
  startedAt: string;
  lastActivityAt: string;
  durationSec: number;
  pageViewCount: number;
  eventCount: number;
  device?: { type?: string; os?: string; osVersion?: string; browser?: string };
  app?: { version?: string; deviceModel?: string; installSource?: string };
  source?: { medium?: string; channel?: string };
  country?: string;
  city?: string;
}

export interface UserDevicesResponse {
  usage: UserUsage | null;
  devices: UserDevice[];
  sessions: UserAnalyticsSession[];
}

/** فیلترهای اختیاری لیست کاربران */
export interface RegularUserFilters {
  search?: string;
  isActive?: boolean;
  isBanned?: boolean;
  /** 'app' | 'web' | 'both' | 'unknown' | 'all' */
  channel?: string;
  /** 'android' | 'ios' | 'web' | 'all' */
  platform?: string;
  appVersion?: string;
  /** 'createdAt' | 'lastActive' | 'lastLogin' */
  sort?: string;
}

// Interface for ban user request
export interface BanUserRequest {
  banReason: string;
}

// Interface for paginated response
export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

// regularUserService for API operations
const regularUserService = {
  // Get all regular users with filtering and pagination
  getRegularUsers: async (
    page: number = 1,
    limit: number = 10,
    search?: string,
    isActive?: boolean,
    isBanned?: boolean,
    // فیلترهای کانال/پلتفرم به‌صورت آبجکت اختیاری اضافه شده‌اند تا امضای
    // موقعیتیِ قبلی نشکند.
    extra?: RegularUserFilters
  ): Promise<PaginatedResponse<RegularUser>> => {
    try {
      // Build query parameters
      const params: any = { page, limit };

      if (search) params.search = search;
      if (isActive !== undefined) params.isActive = isActive;
      if (isBanned !== undefined) params.isBanned = isBanned;
      // 'all' یعنی «بدون فیلتر» — نباید به بک‌اند فرستاده شود.
      if (extra?.channel && extra.channel !== 'all') params.channel = extra.channel;
      if (extra?.platform && extra.platform !== 'all') params.platform = extra.platform;
      if (extra?.appVersion) params.appVersion = extra.appVersion;
      if (extra?.sort) params.sort = extra.sort;

      // Use axios directly to get full response with pagination
      const token = localStorage.getItem('accessToken');
      const { default: axios } = await import('axios');
      const { API_BASE_URL } = await import('../config/constants');

      const response = await axios.get(`${API_BASE_URL}/admin/regular-users`, {
        params,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // Backend returns: { status, results, pagination, data }
      if (response.data && response.data.status === 'success') {
        return {
          data: response.data.data || [],
          pagination: response.data.pagination || { total: 0, page: 1, pages: 1, limit: 10 }
        };
      }

      // Fallback if response format is different
      return {
        data: Array.isArray(response.data) ? response.data : [],
        pagination: { total: 0, page: 1, pages: 1, limit: 10 }
      };
    } catch (error) {
      console.error('Error fetching regular users:', error);
      throw error;
    }
  },
  
  // Get details for a single regular user
  getRegularUserDetails: async (userId: string): Promise<RegularUser> => {
    try {
      // Use axios directly to get full response
      const token = localStorage.getItem('accessToken');
      const { default: axios } = await import('axios');
      const { API_BASE_URL } = await import('../config/constants');

      const response = await axios.get(`${API_BASE_URL}/admin/regular-users/${userId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // Backend returns: { status: 'success', data: user }
      if (response.data && response.data.status === 'success') {
        return response.data.data;
      }

      return response.data;
    } catch (error) {
      console.error('Error fetching user details:', error);
      throw error;
    }
  },
  
  /**
   * دستگاه‌ها و سشن‌های اخیر کاربر — «این کاربر با اپ کار می‌کند یا سایت؟»
   * با همان پرمیشن نمایش جزئیات کاربر محافظت می‌شود.
   */
  getUserDevices: async (userId: string): Promise<UserDevicesResponse> => {
    try {
      const token = localStorage.getItem('accessToken');
      const { default: axios } = await import('axios');
      const { API_BASE_URL } = await import('../config/constants');

      const response = await axios.get(`${API_BASE_URL}/admin/regular-users/${userId}/devices`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data && response.data.status === 'success') {
        return response.data.data;
      }
      return { usage: null, devices: [], sessions: [] };
    } catch (error) {
      console.error('Error fetching user devices:', error);
      throw error;
    }
  },

  // Get audit logs for a user
  getUserAuditLogs: async (
    userId: string,
    page: number = 1,
    limit: number = 20
  ): Promise<PaginatedResponse<any>> => {
    try {
      const token = localStorage.getItem('accessToken');
      const { default: axios } = await import('axios');
      const { API_BASE_URL } = await import('../config/constants');

      const response = await axios.get(`${API_BASE_URL}/admin/regular-users/${userId}/audit-logs`, {
        params: { page, limit },
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data && response.data.status === 'success') {
        return {
          data: response.data.data || [],
          pagination: response.data.pagination || { total: 0, page: 1, pages: 1, limit: 20 }
        };
      }

      return { data: [], pagination: { total: 0, page: 1, pages: 1, limit: 20 } };
    } catch (error) {
      console.error('Error fetching user audit logs:', error);
      throw error;
    }
  },

  // Get login history for a user
  getUserLoginHistory: async (
    userId: string,
    page: number = 1,
    limit: number = 20
  ): Promise<PaginatedResponse<any>> => {
    try {
      const token = localStorage.getItem('accessToken');
      const { default: axios } = await import('axios');
      const { API_BASE_URL } = await import('../config/constants');

      const response = await axios.get(`${API_BASE_URL}/admin/regular-users/${userId}/login-history`, {
        params: { page, limit },
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data && response.data.status === 'success') {
        return {
          data: response.data.data || [],
          pagination: response.data.pagination || { total: 0, page: 1, pages: 1, limit: 20 }
        };
      }

      return { data: [], pagination: { total: 0, page: 1, pages: 1, limit: 20 } };
    } catch (error) {
      console.error('Error fetching user login history:', error);
      throw error;
    }
  },

  // Ban a user
  banUser: async (userId: string, banReason: string): Promise<any> => {
    try {
      const token = localStorage.getItem('accessToken');
      const { default: axios } = await import('axios');
      const { API_BASE_URL } = await import('../config/constants');

      const response = await axios.post(
        `${API_BASE_URL}/admin/regular-users/${userId}/ban`,
        { banReason },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error banning user:', error);
      throw error;
    }
  },

  // Unban a user
  unbanUser: async (userId: string): Promise<any> => {
    try {
      const token = localStorage.getItem('accessToken');
      const { default: axios } = await import('axios');
      const { API_BASE_URL } = await import('../config/constants');

      const response = await axios.post(
        `${API_BASE_URL}/admin/regular-users/${userId}/unban`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error unbanning user:', error);
      throw error;
    }
  }
};

export default regularUserService; 